// 看護師データの型定義
// ChatGPTアシスタントの型定義
export interface ChatGPTAssistant {
  id: number;
  name: string;
  order: number;
  assistant_id: string;
}

// 研修ルームステータスの定義
export const WORKSHOP_ROOM_STATUS_TYPES = {
  DOING: '演習中',
  IN_REVIEW: '承認待ち',
  DONE: '終了'
};
// 研修ルームも研修ステータスと同じ色定義を使用
// 既存の WORKSHOP_STATUS_COLORS, WORKSHOP_STATUS_TEXT_COLORS を使用
// 研修ルームの型定義
export interface WorkshopRoom {
  id: number;
  parent_id: number;
  child_id: number;
  parent_unread_count: number;
  child_unread_count: number;
  send_count: number;
  send_char_count: number;
  workshop_count: number;
  workshop_room_status: string;
  last_chat_message: string | null;
  last_chat_send_at: string | null;
  created_at: string;
  updated_at: string;
  child_assistant: ChatGPTAssistant | null;
}

// サポート看護師（上席）のインターフェース
export interface SupportNurse {
  id: number;
  name: string;
  nickname: string;
  gender_type: string;
  prefecture_type: string;
  role_type: string;
  birthday?: string;
  can_assign?: boolean;
  senior_talk_room_id: number;
  last_chat_send_at: string | null;
  last_chat_message: string | null;
}

// 運営相談ルームのインターフェース
export interface AdminTalkRoom {
  id: number;
  parent_id: number;
  child_id: number;
  can_display: boolean | null;
  last_chat_send_at: string | null;
  last_chat_message: string | null;
  created_at: string;
  updated_at: string;
  child: {
    id: number;
    name: string;
    nickname: string;
    gender_type: string;
    role_type: string;
    prefecture_type: string;
    birthday?: string;
  };
}

export interface Nurse {
  id: number;
  name: string;
  nickname: string;
  birthday: string;
  gender_type: string;
  prefecture_type: string;
  introduction: string;
  matter_of_concern: string | null;
  // 看護師情報
  nurse_license_pass_year: number;
  license_type_other: string | null;
  employment_type: string | null;
  // 経験情報
  nursing_work_experience_type_list: string | null;
  nursing_work_experience_type_other: string | null;
  cancer_nursing_experience_type: string | null;
  // がん経験情報
  cancer_experience: boolean | null;
  cancer_patient_party_type_list: string | null;
  cancer_patient_party_type_other: string | null;
  // 家族情報
  has_partner: boolean | null;
  nurse_children_type_list: string | null;
  nurse_pet_type_list: string | null;
  nurse_pet_type_other: string | null;
  // 業務設定
  can_assign: boolean;
  reply_period_type: string;
  role_type: string;
  workshop_status: string;
  workshop_finish_count: number;
  // 外部連携
  role_id: number | null;
  user_id: string;
  stripe_connect_ext_id: string | null;
  // user_authsテーブルから取得するフィールド
  email: string;
  senior_memo: string | null; // 上席メモ
  resigned_at: string | null; // 退会日時
  reset_password_sent_at?: string | null; // パスワードリセット送信日時
  profile_image_url: string | null; // プロフィール画像URL
  // 管理用
  created_at: string;
  updated_at: string;
  current_sign_in_at: string | null; // 最終ログイン日時
  in_active_talk?: boolean; // 対話状態（フロントエンド表示用、APIの結果に基づいて設定）
  is_test_account: boolean; // テストアカウントかどうか
  workshop_rooms?: WorkshopRoom[];
  latest_workshop_room?: WorkshopRoom; // 最新の研修ルーム情報（一覧表示用）
  related_patients?: any[]; // 対話したことのある患者リスト
  blocked_patients?: any[]; // ブロックしている患者リスト
  support_nurses?: SupportNurse[]; // サポート看護師（上席）リスト
  assigned_nurses?: SupportNurse[]; // 担当看護師リスト（現看護師が上席担当のスタッフ看護師）
  admin_talk_rooms?: AdminTalkRoom[]; // 運営相談ルーム（現看護師がparentの）リスト
  /* ユーザー認証情報のトークン (キー: client_id や client_id_refresh など) */
  tokens?: Record<string, { token: string; expiry: number; token_extras?: any }>;
  /* FCM通知トークン */
  fcm_tokens?: Record<string, any>;
}

// 看護師一覧のレスポンス型
export interface NurseListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Nurse[];
}

// ロール種別
export const ROLE_TYPES = {
  OWNER: 'オーナー',
  MANAGER: 'マネージャー',
  TALK_STAFF: '対話看護師',
  STAFF: '研修看護師'
};
// ロール種別ごとの色設定
export const ROLE_COLORS = {
  OWNER: '#9C27B0',     // 紫（オーナー）
  MANAGER: '#2196F3',   // 青（マネージャー）
  TALK_STAFF: '#4CAF50', // 緑（対話看護師）
  STAFF: '#FF9800'      // オレンジ（研修看護師）
};
// ロール種別の選択肢
export const ROLE_TYPE_OPTIONS = [
  { value: 'OWNER', label: 'オーナー', color: ROLE_COLORS.OWNER },
  { value: 'MANAGER', label: 'マネージャー', color: ROLE_COLORS.MANAGER },
  { value: 'TALK_STAFF', label: '対話看護師', color: ROLE_COLORS.TALK_STAFF },
  { value: 'STAFF', label: '研修看護師', color: ROLE_COLORS.STAFF }
];
// 研修ステータス
export const WORKSHOP_STATUS_TYPES = {
  READY: '開始待ち',
  DOING: '演習中',
  IN_REVIEW: '承認待ち',
  DONE: '終了'
};

// 研修ステータスの定義
export enum WorkshopStatus {
  ALL = '',          // すべて（未選択）
  READY = 'READY',   // 開始待ち
  DOING = 'DOING',   // 演習中
  IN_REVIEW = 'IN_REVIEW', // 承認待ち
  DONE = 'DONE'      // 終了
}

// 研修ステータスの表示名
export const WORKSHOP_STATUS_DISPLAY = {
  [WorkshopStatus.ALL]: 'すべて',
  [WorkshopStatus.READY]: '開始待ち',
  [WorkshopStatus.DOING]: '演習中',
  [WorkshopStatus.IN_REVIEW]: '承認待ち',
  [WorkshopStatus.DONE]: '終了'
};
// 研修ステータスの色定義
export const WORKSHOP_STATUS_COLORS = {
  READY: '#bdbdbd',       // 開始待ち - グレー
  DOING: '#2196f3',       // 演習中 - 青
  IN_REVIEW: '#6a1b9a',   // 承認待ち - 紫
  DONE: '#7cb342'         // 終了 - 緑
};
// 研修ステータスのテキスト色定義
export const WORKSHOP_STATUS_TEXT_COLORS = {
  READY: '#757575',       // 開始待ち - 少し濃いめのグレー
  DOING: '#1976d2',       // 演習中 - 少し濃いめの青
  IN_REVIEW: '#4a148c',   // 承認待ち - より濃い紫
  DONE: '#558b2f'         // 終了 - 少し濃いめの緑
};

// 看護師認証コードの型定義
export interface NurseCertificationCode {
  id: number;
  code: string;
  month: string;
  created_at: string;
  updated_at: string;
}

// 看護師タイプの背景色とボーダー色
export const NURSE_TYPE_THEME = {
  // 通常看護師テーマ
  NORMAL: {
    BACKGROUND: '#ffeef3',      // 薄いピンク
    BORDER: '#ffcdd2',          // ピンクのボーダー
    DARK: '#f50057'             // 濃いピンク - テキストやアクセント用
  },
  // 研修看護師テーマ
  TRAINING: {
    BACKGROUND: '#fff3e0',      // 薄いオレンジ
    BORDER: '#ffe0b2',          // オレンジのボーダー
    DARK: '#ff9800'             // 濃いオレンジ - テキストやアクセント用
  },
  // 共通テーマ（患者一覧などで利用）
  COMMON: {
    BACKGROUND: '#f5f5f5',      // 薄いグレー
    BORDER: '#e0e0e0'           // グレーのボーダー
  }
};
// 返信時間帯
export const REPLY_PERIOD_TYPES = {
  DAYTIME: '日中',
  NIGHT: '夜間',
  IRREGULAR: '不定期'
};
// がん看護経験タイプ
export const CANCER_NURSING_EXPERIENCE_TYPES = {
  MORE_THAN_2_YEAR: '2年以上',
  LESS_THAN_2_YEAR: '2年未満',
  NONE: 'なし'
};
// がん患者関係者タイプ
export const CANCER_PATIENT_PARTY_TYPES = {
  MYSELF: '本人',
  PARENTS: '両親',
  GRANDPARENTS: '祖父母',
  BROTHERS: '兄弟',
  CHILD: '子供',
  FRIEND: '友人',
  PARTNER: '配偶者',
  OTHER: 'その他'
};
// 看護経験タイプ
export const NURSING_WORK_EXPERIENCE_TYPES = {
  PALLIATIVE_CARE_WARD: '緩和ケア病棟',
  HOSPICE: 'ホスピス',
  INTERNAL_MEDICINE: '内科',
  SURGERY: '外科',
  ORTHOPEDIC_SURGERY: '整形外科',
  OTOLARYNGOLOGY: '耳鼻科',
  ORAL_SURGERY: '口腔外科',
  OBSTETRICS: '産科',
  GYNECOLOGY: '婦人科',
  PEDIATRICS: '小児科',
  PSYCHIATRY: '精神科',
  HOME_CARE: '訪問看護',
  HOME_VISIT: '訪問診療',
  INQUIRY_COUNTER: '相談窓口等',
  CORPORATION: '企業での経験',
  OTHER: 'その他'
};
// 子供タイプ
export const NURSE_CHILDREN_TYPES = {
  HIGH_SCHOOL_ABOVE: '高校生以上',
  ELEMENTARY_AND_JUNIOR_HIGH_SCHOOL: '小中学生',
  PRESCHOOL_CHILDREN: '未就学児童'
};
// ペットタイプ
export const NURSE_PET_TYPES = {
  DOG: '犬',
  CAT: '猫',
  FISH: '魚',
  SMALL_BIRD: '小鳥',
  INSECTS: '昆虫類',
  HAMSTER: 'ハムスター',
  OTHER: 'その他'
};

// 対話状態の定義
export enum TalkStatus {
  ALL = '',          // すべて（未選択）
  IN_TALK = 'doing', // 対話中
  WAITING = 'waiting' // 待機中
}

// 対話状態の表示名
export const TALK_STATUS_DISPLAY = {
  [TalkStatus.ALL]: 'すべて',
  [TalkStatus.IN_TALK]: '対話中',
  [TalkStatus.WAITING]: '待機中'
};

// 受付状態の定義
export enum AssignStatus {
  ALL = '',          // すべて（未選択）
  ASSIGNABLE = 'true', // 受付中
  UNASSIGNABLE = 'false' // 停止中
}

// 受付状態の表示名
export const ASSIGN_STATUS_DISPLAY = {
  [AssignStatus.ALL]: 'すべて',
  [AssignStatus.ASSIGNABLE]: '受付中',
  [AssignStatus.UNASSIGNABLE]: '停止中'
};

// 退会状態の定義
export enum ResignedStatus {
  ACTIVE_ONLY = 'false',    // 有効ユーザーのみ
  INCLUDE_RESIGNED = 'true', // 退会済みを含む
  RESIGNED_ONLY = 'only'     // 退会済みのみ
}

// 退会状態の表示名
export const RESIGNED_STATUS_DISPLAY = {
  [ResignedStatus.ACTIVE_ONLY]: '有効ユーザーのみ',
  [ResignedStatus.INCLUDE_RESIGNED]: '退会済みを含む',
  [ResignedStatus.RESIGNED_ONLY]: '退会済みのみ'
};

// ブロック状態の定義
export enum BlockedStatus {
  ALL = '',          // すべて（未選択）
  BLOCKED = 'true',  // ブロック済み
  NOT_BLOCKED = 'false' // 非ブロック
}

// ブロック状態の表示名
export const BLOCKED_STATUS_DISPLAY = {
  [BlockedStatus.ALL]: 'すべて',
  [BlockedStatus.BLOCKED]: '拒否してる',
  [BlockedStatus.NOT_BLOCKED]: '拒否してない'
};

// 上席相談ルーム関係の定義
export enum SeniorTalkRoomStatus {
  ALL = '',                    // すべて（未選択）
  NO_RELATIONSHIP = 'no_relationship' // 関係なし
}

// 上席相談ルーム関係の表示名
export const SENIOR_TALK_ROOM_STATUS_DISPLAY = {
  [SeniorTalkRoomStatus.ALL]: 'すべて',
  [SeniorTalkRoomStatus.NO_RELATIONSHIP]: 'サポートなし'
};

// テストアカウント状態
export enum TestAccountStatus {
  ALL = 'all',
  NORMAL = 'normal',
  TEST = 'test'
}

// テストアカウント状態の表示名
export const TEST_ACCOUNT_STATUS_DISPLAY = {
  [TestAccountStatus.ALL]: 'テストを含む',
  [TestAccountStatus.NORMAL]: '通常のみ',
  [TestAccountStatus.TEST]: 'テストのみ'
};

// 看護師検索パラメータ
export interface NurseSearchParams {
  page?: number;
  page_size?: number;
  search?: string;
  email_search?: string; // メールアドレス専用検索パラメータを追加
  user_id?: string;      // user_id検索パラメータ (通常看護師モードで表示)
  ordering?: string;
  role_type?: string | string[];
  can_assign?: string; // 申込み状態（選択式）
  include_resigned?: string; // 退会状態（選択式）
  talk_status?: string; // 対話状態でフィルタリング
  blocked_status?: string; // ブロック状態でフィルタリング
  workshop_status?: string | string[]; // 研修ステータスでフィルタリング（複数選択可能）
  workshop_finish_count?: string; // 研修修了回数（等しい）
  workshop_finish_count_gte?: string; // 研修修了回数（以上）
  workshop_finish_count_lte?: string; // 研修修了回数（以下）
  workshop_finish_count_operator?: string; // 研修修了回数の比較演算子（eq, gte, lte）
  conversation_count?: string; // 対話回数（等しい）
  conversation_count_gte?: string; // 対話回数（以上）
  conversation_count_lte?: string; // 対話回数（以下）
  conversation_count_operator?: string; // 対話回数の比較演算子（eq, gte, lte）
  is_training_mode?: boolean; // 研修看護師モードかどうか（trueの場合、workshop_roomを結合して取得）
  created_at_start?: string; // 登録日範囲検索（開始日）
  created_at_end?: string; // 登録日範囲検索（終了日）
  senior_talk_room_status?: string; // シニア対話ルーム関係の状態フィルタリング
  is_test_account?: string; // テストアカウントフィルター（all, normal, test）
}
